import { isK8Grade } from 'utils/productHelpers'

/**
 * Extract programCodes from capabilities (platform scope)
 * Capabilities is a required property in v4 Site API payload
 * @param {Array} capabilities - Site capabilities array (required in v4 API)
 * @returns {Array} - Program codes array (empty if no platform capabilities found)
 */
export const extractProgramCodesFromCapabilities = capabilities => {
  if (!Array.isArray(capabilities) || !capabilities.length) {
    return []
  }
  const codes = capabilities
    .filter(
      c =>
        c &&
        c.scope === 'platform' &&
        typeof c.name === 'string' &&
        c.name.length,
    )
    .map(c => c.name)
  return codes
}

/**
 * Normalize claims (lowercase to TitleCase for Teacher/Admin)
 * @param {Array} claims - Claims array
 * @returns {Array} - Normalized claims array
 */
export const normalizeClaims = claims => {
  if (!Array.isArray(claims)) return []
  return claims.filter(Boolean).map(c => {
    if (typeof c !== 'string') return c
    const lc = c.toLowerCase()
    if (lc === 'teacher') return 'Teacher'
    if (lc === 'admin') return 'Admin'
    return c
  })
}

/**
 * Gets available grades for a program from userAccess
 * Extracts individual grades (K, 1-8) from userAccess, excluding tier2 and training
 * @param {Object} userAccess - User access object from state.userAccessManager.access
 * @param {string} programKey - Program key ('k5SelDigital' or 'mspDigital')
 * @returns {string[]} - Array of grade numbers user can access (e.g., ['K', '1', '2', '3', '4', '5'])
 */
export const getGradesForProgramFromUserAccess = (userAccess, programKey) => {
  if (!userAccess || !programKey) return []

  const programAccess = userAccess[programKey]
  if (!programAccess) return []

  // Get all grade numbers (keys) from the program access
  const gradeNumbers = Object.keys(programAccess)

  // Filter to grades where user has teacher or admin role
  const accessibleGrades = gradeNumbers.filter(gradeNumber => {
    const gradeAccess = programAccess[gradeNumber]
    const { admin, teacher } = gradeAccess || {}
    const hasRoleAdmin = admin?.hasRole === true
    const hasRoleTeacher = teacher?.hasRole === true
    return hasRoleAdmin || hasRoleTeacher
  })

  // Filter to only individual grades (K, 1-8), exclude tier2 and training courses
  const individualGrades = accessibleGrades.filter(grade => isK8Grade(grade))

  return individualGrades
}
